import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, Sparkles, Send, Lightbulb, Bot, User,
  Archive, Plus, MessageSquareText, Trash2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

let convCounter = 0;

export default function AIAssistantTab() {
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversations, setConversations] = useState(() => {
    const id = `conv_${++convCounter}`;
    return [{ id, messages: [], label: "Konversation 1", created_at: new Date().toISOString() }];
  });
  const [activeConvId, setActiveConvId] = useState(() => conversations[0]?.id);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const chatEndRef = useRef(null);

  const activeConv = conversations.find(c => c.id === activeConvId);
  const messages = useMemo(() => activeConv?.messages || [], [activeConv]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/admin/ai/insights/history");
      setHistory(res.data);
    } catch (e) {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  const generateInsight = async () => {
    setInsightsLoading(true);
    setSelectedInsight(null);
    try {
      const res = await api.get("/admin/ai/insights");
      const data = res.data;
      const newInsight = {
        id: data.id,
        content: data.insights,
        created_at: data.generated_at,
      };
      setSelectedInsight(newInsight);
      setHistory((prev) => [
        { id: data.id, created_at: data.generated_at },
        ...prev,
      ]);
      toast.success("Ny insight genererad!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.includes("quota")) {
        toast.error("OpenAI-kvoten är slut. Ladda mer krediter på ditt OpenAI-konto.");
      } else if (detail?.includes("API key")) {
        toast.error("OpenAI API key saknas. Kontrollera .env filen.");
      } else {
        toast.error("Kunde inte generera: " + (detail || e.message));
      }
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadInsightFromHistory = async (id) => {
    setSelectedInsight(null);
    try {
      const res = await api.get(`/admin/ai/insights/${id}`);
      setSelectedInsight(res.data);
    } catch (e) {
      toast.error("Kunde inte ladda insight");
    }
  };

  const newConversation = () => {
    const id = `conv_${++convCounter}`;
    const num = conversations.length + 1;
    setConversations(prev => [...prev, { id, messages: [], label: `Konversation ${num}`, created_at: new Date().toISOString() }]);
    setActiveConvId(id);
  };

  const deleteConversation = (id) => {
    setConversations(prev => {
      const filtered = prev.filter(c => c.id !== id);
      if (activeConvId === id && filtered.length > 0) {
        setActiveConvId(filtered[filtered.length - 1].id);
      } else if (filtered.length === 0) {
        const newId = `conv_${++convCounter}`;
        const newConv = { id: newId, messages: [], label: "Konversation 1", created_at: new Date().toISOString() };
        setActiveConvId(newId);
        return [newConv];
      }
      return filtered;
    });
  };

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim() || asking) return;

    const userMsg = { role: "user", content: question.trim() };
    setConversations(prev => prev.map(c =>
      c.id === activeConvId ? { ...c, messages: [...c.messages, userMsg] } : c
    ));
    setQuestion("");
    setAsking(true);

    try {
      // Send prior turns so follow-ups like "ändra den till 99 kr" work.
      const history = messages.slice(-12).map(m => ({ role: m.role, content: m.content }));
      const res = await api.post("/admin/ai/ask", { question: userMsg.content, history });
      const botMsg = { role: "assistant", content: res.data.answer, proposals: res.data.proposals || [] };
      setConversations(prev => prev.map(c =>
        c.id === activeConvId ? { ...c, messages: [...c.messages, botMsg] } : c
      ));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errMsg = detail?.includes("quota")
        ? "OpenAI-kvoten är slut. Ladda mer krediter."
        : detail?.includes("API key")
        ? "OpenAI API key saknas eller är ogiltig."
        : detail || "Kunde inte få svar.";
      setConversations(prev => prev.map(c =>
        c.id === activeConvId ? { ...c, messages: [...c.messages, { role: "assistant", content: `❌ ${errMsg}` }] } : c
      ));
    } finally {
      setAsking(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("sv-SE", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      {/* Archive Sidebar — last on mobile, left column on desktop */}
      <div className="order-3 lg:order-1 w-full lg:w-64 shrink-0 bg-[#121212] rounded-xl border border-[#27272A] p-4 flex flex-col max-h-80 lg:max-h-none lg:h-[calc(100vh-220px)]">
        <div className="flex items-center gap-2 mb-4">
          <Archive className="w-4 h-4 text-[#D4B483]" />
          <h3 className="text-sm font-semibold text-[#EDEDED]">Arkiv</h3>
        </div>

        <Button
          onClick={generateInsight}
          disabled={insightsLoading}
          className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-9 text-sm mb-4"
        >
          {insightsLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-1" />
          ) : (
            <Plus className="w-4 h-4 mr-1" />
          )}
          {insightsLoading ? "Genererar..." : "Generera insight"}
        </Button>

        <ScrollArea className="flex-1 -mr-2 pr-2">
          {historyLoading && history.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 text-[#A8ABB2] animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-[#A8ABB2] text-xs text-center py-8">
              Inga sparade insikter än
            </p>
          ) : (
            <div className="space-y-1">
              {history.map((item) => (
                <button
                  key={item.id}
                  onClick={() => loadInsightFromHistory(item.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${
                    selectedInsight?.id === item.id
                      ? "bg-[#D4B483]/15 text-[#D4B483]"
                      : "text-[#D4D4D8] hover:bg-[#1A1A1A] hover:text-[#EDEDED]"
                  }`}
                >
                  <MessageSquareText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{formatDate(item.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="order-1 lg:order-2 flex-1 min-w-0 space-y-5">
        {insightsLoading ? (
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-8">
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#D4B483] animate-spin" />
              <span className="ml-4 text-[#D4D4D8]">Analyserar data och genererar insikter...</span>
            </div>
          </div>
        ) : selectedInsight ? (
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-6">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="w-5 h-5 text-[#D4B483]" />
              <h2 className="text-lg font-semibold text-[#EDEDED]">AI-insikter</h2>
            </div>
            <p className="text-[#A8ABB2] text-xs mb-5">
              Genererad {formatDate(selectedInsight.created_at)}
            </p>
            <div className="prose prose-invert max-w-none text-sm text-[#E8E8EA] leading-relaxed whitespace-pre-wrap">
              {selectedInsight.content}
            </div>
          </div>
        ) : (
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-8">
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Sparkles className="w-12 h-12 text-[#27272A] mb-4" />
              <p className="text-[#D4D4D8] text-sm font-medium mb-1">
                Inga insikter visas
              </p>
              <p className="text-[#A8ABB2] text-xs mb-6">
                Klicka på "Generera insight" för att få AI-drivna rekommendationer
              </p>
              <Button
                onClick={generateInsight}
                disabled={insightsLoading}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generera första insight
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Chat Sidebar — below the insights on mobile, right column on desktop */}
      <div className="order-2 lg:order-3 w-full lg:w-80 shrink-0 bg-[#121212] rounded-xl border border-[#27272A] p-4 flex flex-col h-[70vh] lg:h-[calc(100vh-220px)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-[#D4B483]" />
            <h3 className="text-sm font-semibold text-[#EDEDED]">Konversationer</h3>
          </div>
          <Button
            onClick={newConversation}
            size="sm"
            className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-8 px-2.5 text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Ny
          </Button>
        </div>

        <div className="space-y-1 mb-3">
          {conversations.map(conv => (
            <div key={conv.id} className="flex items-center gap-1">
              <button
                onClick={() => setActiveConvId(conv.id)}
                className={`flex-1 text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${
                  activeConvId === conv.id
                    ? "bg-[#D4B483]/15 text-[#D4B483]"
                    : "text-[#D4D4D8] hover:bg-[#1A1A1A] hover:text-[#EDEDED]"
                }`}
              >
                <MessageSquareText className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{conv.label}</span>
              </button>
              {conversations.length > 1 && (
                <button
                  onClick={() => deleteConversation(conv.id)}
                  className="p-1.5 rounded-lg text-[#A8ABB2] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex-1 flex flex-col min-h-0 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
          <ScrollArea className="flex-1 px-3 py-3">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <Sparkles className="w-6 h-6 text-[#27272A] mb-2" />
                <p className="text-[#A8ABB2] text-xs px-2">
                  Fråga — eller be mig göra ändringar: "höj priset på Shrimp Wrap till 129 kr", "stäng på söndag", "skapa rabattkod SOMMAR10 på 10%"...
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
                    {msg.role === "assistant" && (
                      <div className="w-6 h-6 rounded-full bg-[#D4B483]/20 flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3 h-3 text-[#D4B483]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                        msg.role === "user"
                          ? "bg-[#D4B483] text-[#0A0A0A]"
                          : "bg-[#1A1A1A] text-[#E8E8EA] border border-[#27272A]"
                      }`}
                    >
                      {msg.proposals?.length > 0 && (
                        <div className="mb-2 space-y-1">
                          {msg.proposals.map((p, pi) => (
                            <div key={pi} className="flex items-start gap-1.5 bg-[#D4B483]/10 border border-[#D4B483]/30 rounded-md px-2 py-1 text-[11px]">
                              <span className="shrink-0">📋</span>
                              <span className="text-[#E5CFA5]">
                                Förslag: {p.title}
                                {p.confidence != null ? ` (säkerhet ${Math.round(p.confidence)}%)` : ""}
                                {" — godkänn under Förslag"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.content}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-6 h-6 rounded-full bg-[#D4B483] flex items-center justify-center shrink-0 mt-0.5">
                        <User className="w-3 h-3 text-[#0A0A0A]" />
                      </div>
                    )}
                  </div>
                ))}
                {asking && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#D4B483]/20 flex items-center justify-center shrink-0">
                      <Bot className="w-3 h-3 text-[#D4B483]" />
                    </div>
                    <div className="bg-[#1A1A1A] border border-[#27272A] rounded-lg px-3 py-2">
                      <Loader2 className="w-3 h-3 text-[#D4B483] animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </ScrollArea>

          <form onSubmit={handleAsk} className="flex gap-2 p-3 border-t border-[#27272A]">
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Fråga eller be mig ändra något..."
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-9 text-xs flex-1"
              disabled={asking}
            />
            <Button
              type="submit"
              disabled={!question.trim() || asking}
              className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-9 w-9 p-0"
            >
              {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
